import { Fragment, useRef, useState } from "react";
import { Link } from "react-router";
import type { Me } from "../api";
import { CopyButton } from "../components";
import {
  GUIDES,
  OPEN_TERMINAL,
  PLACEHOLDER,
  TOOL_ORDER,
  WSL_NOTE,
  resolve,
  type Block,
  type Ctx,
  type Guide,
  type OsPart,
  type Step,
  type Tool,
  type TroubleRow,
} from "../guides/content";
import { OS_LABEL, OS_LIST, detectOs, rememberOs, rememberedOs, type OS } from "../guides/os";
import { useForgeEvent } from "../hooks";
import { secretMemory } from "../secretMemory";

/**
 * The in-app form of the setup guides. The words and commands live in ../guides/content.ts, which
 * is also published to each guide's Confluence page (TAI-2569); guides.test.ts fails until a
 * change is republished. Link to Confluence, never the repository: most employees can't read it.
 */
const CONFLUENCE = "https://tylertech.atlassian.net/wiki/spaces/TA1/pages";

function initialOs(): { os: OS; detected: OS | null } {
  const detected = detectOs(typeof navigator === "undefined" ? undefined : navigator);
  const saved = rememberedOs(typeof localStorage === "undefined" ? undefined : localStorage);
  return { os: saved ?? detected ?? "macos", detected };
}

export function SetupPage({ me }: { me: Me }) {
  const [tool, setTool] = useState<Tool>("claude-code");
  const [{ os, detected }, setOsState] = useState(initialOs);
  const tabs = useRef<HTMLElement>(null);
  const osToggle = useRef<HTMLElement>(null);
  useForgeEvent(tabs, "forge-tab-bar-change", (e: CustomEvent<{ index: number }>) => {
    setTool(TOOL_ORDER[e.detail.index] ?? "claude-code");
  });
  useForgeEvent(osToggle, "forge-button-toggle-group-change", (e: CustomEvent) => {
    const next = OS_LIST.find((o) => o === e.detail);
    if (!next) return;
    setOsState((s) => ({ ...s, os: next }));
    rememberOs(typeof localStorage === "undefined" ? undefined : localStorage, next);
  });
  const remembered = secretMemory.get();
  const key = remembered?.key ?? PLACEHOLDER;
  const host = me.gatewayUrl.replace(/\/$/, "");
  const ctx: Ctx = { host, key };

  return (
    <>
      {remembered ? (
        <forge-inline-message theme="success">
          <forge-icon slot="icon" name="check_circle" />
          <div slot="title">Filled in with the key you just created</div>
          <span>Only this browser tab knows it; reloading the page removes it from these instructions.</span>
        </forge-inline-message>
      ) : (
        <forge-inline-message theme="info">
          <forge-icon slot="icon" name="info" />
          <div slot="title">Paste your key where it says {PLACEHOLDER}</div>
          <span>No key yet? <Link to="/keys?create=1">Create one</Link> and come straight back: it is filled in for you.</span>
        </forge-inline-message>
      )}

      <div className="row gap-medium" style={{ flexWrap: "wrap" }}>
        <b id="setup-os-label">Your computer</b>
        <forge-button-toggle-group ref={osToggle} value={os} mandatory dense aria-labelledby="setup-os-label">
          {OS_LIST.map((o) => <forge-button-toggle key={o} value={o}>{OS_LABEL[o]}</forge-button-toggle>)}
        </forge-button-toggle-group>
        <span className="muted">
          {detected ? (detected === os ? `We think this is a ${OS_LABEL[detected]} computer. ` : `We guessed ${OS_LABEL[detected]}; you chose ${OS_LABEL[os]}. `) : "We could not tell which computer you use. "}
          Every command below is for {OS_LABEL[os]}. {os === "windows" ? "Using WSL (Linux inside Windows)? Choose Linux." : "Wrong? Switch it."}
        </span>
      </div>

      <forge-card>
        <forge-tab-bar ref={tabs} active-tab="0">
          {TOOL_ORDER.map((t) => <forge-tab key={t}>{GUIDES[t].tab}</forge-tab>)}
        </forge-tab-bar>
        <div className="stack gap-medium" style={{ paddingBlockStart: "var(--forge-spacing-large)" }}>
          <GuideView key={`${tool}-${os}`} guide={GUIDES[tool]} os={os} ctx={ctx} />
        </div>
      </forge-card>

      <p className="muted">
        Not sure which model to pick? <Link to="/models">See the models you can use</Link>, with prices, context windows and what each is best for.
      </p>
    </>
  );
}

/** **bold** and `code`, and nothing else. Never inserts markup from the text itself. */
function Rich({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(https:\/\/[^)\s]+\))/g);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**")) return <b key={i}>{p.slice(2, -2)}</b>;
        if (p.startsWith("`") && p.endsWith("`")) return <code key={i} className="mono">{p.slice(1, -1)}</code>;
        const link = /^\[([^\]]+)\]\((https:\/\/[^)\s]+)\)$/.exec(p);
        if (link) return <a key={i} href={link[2]} target="_blank" rel="noreferrer">{link[1]}</a>;
        return p.split("\n").map((line, j) => <Fragment key={`${i}-${j}`}>{j > 0 && <br />}{line}</Fragment>);
      })}
    </>
  );
}

function Para({ text }: { text: string }) {
  return <p className="forge-typography--body2"><Rich text={text} /></p>;
}

function Code({ block, ctx }: { block: Block; ctx: Ctx }) {
  const text = resolve(block.code, ctx);
  return (
    <div className="stack gap-xsmall">
      <pre className="code mono" tabIndex={0}>{text}</pre>
      {block.lang !== "text" && <div><CopyButton text={text} /></div>}
    </div>
  );
}

function Warn({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <forge-inline-message theme="warning">
      <forge-icon slot="icon" name="warning" />
      <div slot="title">{title}</div>
      <span>{children}</span>
    </forge-inline-message>
  );
}

function Details({ summary, children }: { summary: string; children: React.ReactNode }) {
  return (
    <details className="stack gap-small">
      <summary className="muted" style={{ cursor: "pointer" }}>{summary}</summary>
      <div className="stack gap-small" style={{ paddingInlineStart: "var(--forge-spacing-medium)" }}>{children}</div>
    </details>
  );
}

function TestedBanner({ guide, os }: { guide: Guide; os: OS }) {
  const t = guide.tested[os];
  const label = os === "windows" ? "Windows" : OS_LABEL[os];
  const theme = t.status === "tested" ? "success" : t.status === "partly" ? "info" : "warning";
  const title =
    t.status === "tested" ? `Tested on ${label}` : t.status === "partly" ? `Partly tested on ${label}` : `Not tested on ${label}`;
  return (
    <forge-inline-message theme={theme}>
      <forge-icon slot="icon" name={t.status === "untested" ? "warning" : "check_circle"} />
      <div slot="title">{title}</div>
      <span>
        {t.status === "untested"
          ? `We have not run these steps on ${label}. If one fails, post what you saw in the AI Gateway Early Adopters channel in Teams. `
          : t.status === "partly"
            ? `Some of these steps were run on ${label}; the rest are from the tool’s own documentation. `
            : ""}
        <Details summary="What was tested"><Para text={t.detail} /></Details>
      </span>
    </forge-inline-message>
  );
}

function OsBits({ part, ctx }: { part: OsPart | undefined; ctx: Ctx }) {
  if (!part) return null;
  return (
    <>
      {part.say?.map((s, i) => <Para key={i} text={s} />)}
      {part.blocks?.map((b, i) => <Code key={i} block={b} ctx={ctx} />)}
      {part.after?.map((s, i) => <Para key={`a${i}`} text={s} />)}
      {part.caution && <Warn title="Heads up">{part.caution}</Warn>}
    </>
  );
}

function StepView({ n, step, os, ctx }: { n: number; step: Step; os: OS; ctx: Ctx }) {
  return (
    <section className="stack gap-small">
      <h3 className="forge-typography--heading2 card-title">{n}. {step.title}</h3>
      {step.plain?.map((p, i) => <Para key={i} text={p} />)}
      {step.download && (
        <div>
          <forge-button variant="outlined">
            <a href={`/api/setup-files/${step.download.file}`} download={step.download.file}>{step.download.label}</a>
          </forge-button>
        </div>
      )}
      <OsBits part={step.byOs?.[os]} ctx={ctx} />
      {step.blocks?.map((b, i) => <Code key={i} block={b} ctx={ctx} />)}
      {step.warn && <Warn title={step.warn.title}><Rich text={step.warn.text} /></Warn>}
      {step.check && (
        <p className="forge-typography--body2">
          <b>Check it worked: </b><Rich text={step.check} />
        </p>
      )}
      {step.engineers && (
        <Details summary="For engineers">
          {step.engineers.map((e, i) => (typeof e === "string" ? <Para key={i} text={e} /> : <Code key={i} block={e} ctx={ctx} />))}
        </Details>
      )}
    </section>
  );
}

function Trouble({ rows, os }: { rows: TroubleRow[]; os: OS }) {
  const shown = rows.filter((r) => !r.os || r.os.includes(os));
  const groups = [...new Set(shown.map((r) => r.group))];
  return (
    <Details summary="Something went wrong? Find what you see">
      {groups.map((g) => (
        <div key={g} className="stack gap-small">
          <h4 className="forge-typography--heading1" style={{ margin: 0 }}>{g}</h4>
          <table className="forge-data-table">
            <thead><tr><th>What you see</th><th>What it means</th><th>What to do</th></tr></thead>
            <tbody>
              {shown.filter((r) => r.group === g).map((r) => (
                <tr key={r.see}>
                  <td><Rich text={r.see} /></td>
                  <td><Rich text={r.means} /></td>
                  <td><Rich text={r.fix} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      <p className="muted">When you ask for help, send which tool, which step, the exact error text, roughly when it happened, and a screenshot <b>after checking it does not show your key</b>. Never send the key itself.</p>
    </Details>
  );
}

function GuideView({ guide, os, ctx }: { guide: Guide; os: OS; ctx: Ctx }) {
  const next = guide.steps.length;
  return (
    <>
      <Para text={`${guide.intro} ${guide.time}.`} />
      {guide.notice && <Warn title={guide.notice.title}><Rich text={guide.notice.text} /></Warn>}
      <TestedBanner guide={guide} os={os} />
      <section className="stack gap-small">
        <h3 className="forge-typography--heading2 card-title">Before you start</h3>
        <ul className="stack gap-xsmall">
          {guide.need.filter((n) => os === "macos" || !n.startsWith("macOS only")).map((n, i) => <li key={i}><Rich text={n} /></li>)}
          <li><Rich text={`To open a terminal on ${OS_LABEL[os]}: ${OPEN_TERMINAL[os]}`} /></li>
        </ul>
        {os === "windows" && <p className="muted"><Rich text={WSL_NOTE} /></p>}
      </section>
      {guide.steps.map((s, i) => <StepView key={s.title} n={i + 1} step={s} os={os} ctx={ctx} />)}
      {guide.after && (
        <Details summary={guide.after.title}>
          {guide.after.steps.map((s, i) => <StepView key={s.title} n={next + i + 1} step={s} os={os} ctx={ctx} />)}
        </Details>
      )}
      <section className="stack gap-small">
        <h3 className="forge-typography--heading2 card-title">Day to day</h3>
        <ul className="stack gap-xsmall">{guide.everyday.map((e, i) => <li key={i}><Rich text={e} /></li>)}</ul>
      </section>
      <Trouble rows={guide.trouble} os={os} />
      <p className="muted">
        The full guide, for every computer, with what was tested:{" "}
        <a href={`${CONFLUENCE}/${guide.confluencePageId}`} target="_blank" rel="noreferrer">{guide.tab} Setup on Confluence</a>
      </p>
    </>
  );
}
